import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createGtmPlay,
  ensureGtmSequenceRule,
  getGtmPlay,
  getGtmSequenceNodeActivity,
  getGtmSequenceTemplate,
  listGtmPlays,
  patchGtmPlay,
  patchGtmSequenceTemplate,
  type GtmPlay,
  type GtmPlayStatus,
  type GtmSequenceTemplate,
  type GtmSequenceTemplateMessagePatch,
} from "../api/GtmApi";
import { gtmQueryKeys } from "./gtmQueryKeys";

export function useGtmPlaysQuery(workspaceId: string, status: GtmPlayStatus | "all" = "all") {
  return useQuery({
    queryKey: gtmQueryKeys.plays(workspaceId, status),
    queryFn: () => listGtmPlays(workspaceId, status),
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: false,
    staleTime: 15_000,
  });
}

export function useGtmPlayQuery(workspaceId: string, playId: string) {
  return useQuery({
    queryKey: gtmQueryKeys.play(workspaceId, playId),
    queryFn: () => getGtmPlay(workspaceId, playId),
    enabled: Boolean(workspaceId && playId),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
}

export function useGtmPlayCreateMutation(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { audience: string; name?: string }) => createGtmPlay(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gtmQueryKeys.plays(workspaceId) });
    },
  });
}

export function useGtmPlayPatchMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Pick<GtmPlay, "name" | "audience" | "angle" | "status">>) =>
      patchGtmPlay(workspaceId, playId, body),
    onSuccess: (play: GtmPlay) => {
      queryClient.setQueryData(gtmQueryKeys.play(workspaceId, playId), play);
      void queryClient.invalidateQueries({ queryKey: gtmQueryKeys.plays(workspaceId) });
    },
  });
}

export function useGtmSequenceTemplateQuery(workspaceId: string, playId: string) {
  return useQuery({
    queryKey: gtmQueryKeys.sequenceTemplate(workspaceId, playId),
    queryFn: () => getGtmSequenceTemplate(workspaceId, playId),
    enabled: Boolean(workspaceId && playId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useGtmSequenceTemplatePatchMutation(workspaceId: string, playId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: GtmSequenceTemplateMessagePatch) =>
      patchGtmSequenceTemplate(workspaceId, playId, body),
    onSuccess: (template: GtmSequenceTemplate) => {
      queryClient.setQueryData(gtmQueryKeys.sequenceTemplate(workspaceId, playId), template);
    },
  });
}

export function useGtmEnsureSequenceRuleMutation(workspaceId: string) {
  return useMutation({
    mutationFn: () => ensureGtmSequenceRule(workspaceId),
  });
}

export function useGtmSequenceNodeActivityQuery(
  workspaceId: string,
  playId: string,
  step: string | null
) {
  return useQuery({
    queryKey: gtmQueryKeys.sequenceActivity(workspaceId, playId, step || ""),
    queryFn: () => getGtmSequenceNodeActivity(workspaceId, playId, step!),
    enabled: Boolean(workspaceId && playId && step),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
}
